import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { financiamentosApi, categoriasApi, contasApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select, Toggle } from '../../components/ui/Input'
import { Card, CardHeader, CardBody, KpiCard, ProgressBar } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, ErrorState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import {
  StatusParcelaFinanciamento, STATUS_FINANCIAMENTO_LABEL, STATUS_PARCELA_FINANCIAMENTO_LABEL,
  IndiceCorrecaoMonetaria, INDICE_CORRECAO_LABEL, EstrategiaAmortizacaoExtra, ESTRATEGIA_AMORTIZACAO_LABEL,
  SistemaAmortizacao, SISTEMA_AMORTIZACAO_LABEL,
} from '../../lib/enums'
import { formatMoney, formatDate } from '../../lib/utils'
import type { ParcelaFinanciamento, AmortizacaoExtraResultado, AmortizacaoExtraLancada, Financiamento } from '../../types'

const AXIS_TICK = { fontSize: 11, fill: '#8B949E' }

function kFormatter(v: number) {
  return v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
}

export function FinanciamentoDetalhePage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()

  const [pagando, setPagando] = useState<ParcelaFinanciamento | null>(null)
  const [pagamento, setPagamento] = useState({ dataPagamento: new Date().toISOString().slice(0, 10), desconto: '', multa: '' })
  const [editando, setEditando] = useState<ParcelaFinanciamento | null>(null)
  const [edicao, setEdicao] = useState({ data: '', valor: '' })
  const [quitando, setQuitando] = useState(false)
  const [quitacao, setQuitacao] = useState({ valorQuitacao: '', data: new Date().toISOString().slice(0, 10) })
  const [marcandoEmMassa, setMarcandoEmMassa] = useState(false)
  const [emMassa, setEmMassa] = useState({ ate: '', ateNumero: '', dataPagamento: '' })
  const [amortizandoExtra, setAmortizandoExtra] = useState(false)
  const [gerenciandoExtras, setGerenciandoExtras] = useState(false)
  const [corrigindoManual, setCorrigindoManual] = useState(false)
  const [editandoFinanciamento, setEditandoFinanciamento] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const { data: f, isLoading, isError } = useQuery({
    queryKey: ['financiamentos', id],
    queryFn: () => financiamentosApi.obter(id),
    enabled: !!id,
  })

  function invalidar() {
    qc.invalidateQueries({ queryKey: ['financiamentos', id] })
    qc.invalidateQueries({ queryKey: ['financiamentos'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
  }

  const pagar = useMutation({
    mutationFn: () => financiamentosApi.pagarParcela(id, pagando!.id, {
      dataPagamento: pagamento.dataPagamento,
      desconto: pagamento.desconto ? Number(pagamento.desconto) : undefined,
      multa: pagamento.multa ? Number(pagamento.multa) : undefined,
    }),
    onSuccess: () => { invalidar(); toast.success('Parcela paga.'); setPagando(null) },
    onError: (e: Error) => setErro(e.message),
  })

  const editar = useMutation({
    mutationFn: () => financiamentosApi.editarParcela(id, editando!.id, {
      data: edicao.data || undefined,
      valor: edicao.valor ? Number(edicao.valor) : undefined,
    }),
    onSuccess: () => { invalidar(); toast.success('Parcela atualizada.'); setEditando(null) },
    onError: (e: Error) => setErro(e.message),
  })

  const toggleLancamento = useMutation({
    mutationFn: (p: ParcelaFinanciamento) => financiamentosApi.toggleLancamento(id, p.id, !p.gerarLancamento),
    onSuccess: () => { invalidar(); toast.success('Preferência da parcela atualizada.') },
    onError: (e: Error) => toast.error(e.message),
  })

  const quitar = useMutation({
    mutationFn: () => financiamentosApi.quitar(id, { valorQuitacao: Number(quitacao.valorQuitacao), data: quitacao.data }),
    onSuccess: () => { invalidar(); toast.success('Financiamento quitado.'); setQuitando(false) },
    onError: (e: Error) => setErro(e.message),
  })

  const marcarEmMassa = useMutation({
    mutationFn: () => financiamentosApi.marcarParcelasPagasEmMassa(id, {
      ate: emMassa.ate || undefined,
      ateNumero: emMassa.ateNumero ? Number(emMassa.ateNumero) : undefined,
      dataPagamento: emMassa.dataPagamento || undefined,
    }),
    onSuccess: (qtd) => { invalidar(); toast.success(`${qtd} parcela(s) marcada(s) como paga(s).`); setMarcandoEmMassa(false) },
    onError: (e: Error) => setErro(e.message),
  })

  if (isLoading) return <LoadingState />
  if (isError || !f) return <ErrorState message="Financiamento não encontrado." />

  const d = f.dashboard
  const progresso = d.parcelasTotal > 0 ? (d.parcelasPagas / d.parcelasTotal) * 100 : 0
  const proximaPendenteId = f.parcelas.find((p) => !p.ehEntrada && p.status === StatusParcelaFinanciamento.Pendente)?.id

  return (
    <>
      <PageHeader title={f.descricao} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconButton icon="ri-arrow-left-line" label="Voltar" tone="muted" onClick={() => navigate('/financiamentos')} />
          <Badge variant={f.status === 1 ? 'income' : 'muted'}>{STATUS_FINANCIAMENTO_LABEL[f.status]}</Badge>
          {d.emAtraso && <Badge variant="expense">Em atraso</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="light" size="sm" icon="ri-pencil-fill" onClick={() => setEditandoFinanciamento(true)}>Editar</Button>
          <Button variant="soft-info" size="sm" icon="ri-list-check-3" onClick={() => setMarcandoEmMassa(true)}>Marcar parcelas antigas como pagas</Button>
          {f.status === 1 && <Button variant="soft-primary" size="sm" icon="ri-coins-line" onClick={() => setAmortizandoExtra(true)}>Amortizar extra</Button>}
          <Button variant="light" size="sm" icon="ri-file-list-3-line" onClick={() => setGerenciandoExtras(true)}>Amortizações extras lançadas</Button>
          {f.status === 1 && f.indiceCorrecao !== IndiceCorrecaoMonetaria.Nenhum && (
            <Button variant="soft-info" size="sm" icon="ri-pencil-ruler-2-line" onClick={() => setCorrigindoManual(true)}>Corrigir índice manualmente</Button>
          )}
          {f.status === 1 && <Button variant="soft-danger" size="sm" icon="ri-flashlight-line" onClick={() => setQuitando(true)}>Quitar antecipadamente</Button>}
        </div>
      </div>

      {f.sistemaAmortizacao !== 1 && (
        <p className="mb-4 text-13 text-muted">
          Taxa: <strong className="text-white">{((f.taxaJurosMensal ?? 0) * 100).toFixed(2)}% a.m.</strong>
          {f.taxaJurosAnualEquivalente != null && <> (equivale a <strong className="text-white">{(f.taxaJurosAnualEquivalente * 100).toFixed(2)}% a.a.</strong>)</>}
          {f.indiceCorrecao !== IndiceCorrecaoMonetaria.Nenhum && <> · Corrigido por <strong className="text-white">{INDICE_CORRECAO_LABEL[f.indiceCorrecao]}</strong></>}
        </p>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Pago" value={formatMoney(d.valorPagoTotal)} icon="ri-checkbox-circle-line" tone="income"
          footer={<p className="mt-3 text-2xs text-muted">{d.parcelasPagas} de {d.parcelasTotal} parcelas <ProgressBar value={progresso} tone="income" className="mt-1.5" /></p>} />
        <KpiCard title="Saldo devedor" value={formatMoney(d.saldoDevedor)} icon="ri-hourglass-line" tone="warning"
          footer={<p className="mt-3 text-2xs text-muted">Total ainda a pagar (com juros futuros): {formatMoney(d.valorRestante)}</p>} />
        <KpiCard title="Próxima parcela" value={d.proximaParcela ? formatMoney(d.proximaParcela.valor) : '—'} icon="ri-calendar-event-line" tone="info"
          footer={d.proximaParcela && <p className="mt-3 text-2xs text-muted">Vence em {formatDate(d.proximaParcela.dataPrevista)}</p>} />
        <KpiCard title="Juros pagos" value={formatMoney(d.jurosPagoTotal)} icon="ri-percent-line" tone="primary"
          footer={<p className="mt-3 text-2xs text-muted">Amortizado: {formatMoney(d.amortizadoTotal)} · Restam {formatMoney(d.jurosRestanteTotal)} de juros</p>} />
      </div>

      {(d.descontoAcumulado > 0 || d.multaAcumulada > 0) && (
        <p className="mb-6 text-13 text-muted">
          Desconto acumulado: <strong className="text-income">{formatMoney(d.descontoAcumulado)}</strong> · Multa acumulada: <strong className="text-expense">{formatMoney(d.multaAcumulada)}</strong>
        </p>
      )}

      {d.evolucaoSaldoDevedor.length > 0 && (
        <Card className="mb-6">
          <CardHeader title="Evolução do saldo devedor" icon="ri-line-chart-line" />
          <CardBody>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={d.evolucaoSaldoDevedor} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="numero" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} width={38} />
                <Tooltip
                  contentStyle={{ background: '#161B22', border: '1px solid #2D3748', borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => [formatMoney(Number(v)), 'Saldo devedor']}
                  labelFormatter={(n, payload) => {
                    const ponto = payload?.[0]?.payload as { data?: string } | undefined
                    return `Parcela ${n}${ponto?.data ? ` · ${formatDate(ponto.data)}` : ''}`
                  }}
                />
                <Line type="monotone" dataKey="saldoDevedor" stroke="#FFB300" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      {d.evolucaoSaldoDevedor.length > 0 && (
        <Card className="mb-6">
          <CardHeader title="Juros x amortização por parcela" icon="ri-bar-chart-2-line" />
          <CardBody>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.evolucaoSaldoDevedor} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="numero" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} width={38} />
                <Tooltip
                  contentStyle={{ background: '#161B22', border: '1px solid #2D3748', borderRadius: 6, fontSize: 12 }}
                  formatter={(v, name) => [formatMoney(Number(v)), name]}
                  labelFormatter={(n, payload) => {
                    const ponto = payload?.[0]?.payload as { data?: string } | undefined
                    return `Parcela ${n}${ponto?.data ? ` · ${formatDate(ponto.data)}` : ''}`
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#8B949E' }} iconType="circle" />
                <Bar dataKey="amortizacao" name="Amortização" stackId="parcela" fill="#22C55E" />
                <Bar dataKey="juros" name="Juros" stackId="parcela" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Parcelas" icon="ri-list-check-2" />
        <Table>
          <THead>
            <tr>
              <Th>#</Th><Th>Data prevista</Th><Th>Data paga</Th><Th>Status</Th>
              <Th className="text-right">Valor</Th><Th className="text-right">Juros</Th><Th className="text-right">Amortização</Th><Th className="text-right">Correção</Th>
              <Th className="text-right">Pago</Th><Th>Lançamento</Th><Th className="text-right">Ações</Th>
            </tr>
          </THead>
          <tbody>
            {f.parcelas.map((p) => (
              <Tr key={p.id}>
                <Td>{p.ehEntrada ? 'Entrada' : p.numero}</Td>
                <Td className="whitespace-nowrap">{formatDate(p.dataPrevista)}</Td>
                <Td className="whitespace-nowrap text-muted">{p.dataPagamento ? formatDate(p.dataPagamento) : '—'}</Td>
                <Td>
                  <Badge variant={p.status === StatusParcelaFinanciamento.Paga ? 'income' : p.atrasada ? 'expense' : 'muted'}>
                    {p.atrasada ? 'Atrasada' : STATUS_PARCELA_FINANCIAMENTO_LABEL[p.status]}
                  </Badge>
                </Td>
                <Td className="whitespace-nowrap text-right text-white">{formatMoney(p.valor)}</Td>
                <Td className="whitespace-nowrap text-right text-muted">{p.juros != null ? formatMoney(p.juros) : '—'}</Td>
                <Td className="whitespace-nowrap text-right text-muted">{p.amortizacao != null ? formatMoney(p.amortizacao) : '—'}</Td>
                <Td className="whitespace-nowrap text-right text-muted">{p.correcaoMonetaria != null ? formatMoney(p.correcaoMonetaria) : '—'}</Td>
                <Td className="whitespace-nowrap text-right text-muted">{p.valorPago ? formatMoney(p.valorPago) : '—'}</Td>
                <Td>
                  <Toggle checked={p.gerarLancamento} onChange={() => toggleLancamento.mutate(p)} disabled={toggleLancamento.isPending} />
                </Td>
                <Td className="whitespace-nowrap text-right">
                  {p.status === StatusParcelaFinanciamento.Pendente && (
                    <>
                      <IconButton
                        icon="ri-pencil-fill" label={`Editar parcela ${p.numero}`} tone="info"
                        onClick={() => { setEditando(p); setEdicao({ data: p.dataPrevista.slice(0, 10), valor: String(p.valor) }); setErro(null) }}
                      />
                      <IconButton
                        icon="ri-check-line"
                        label={p.ehEntrada || p.id === proximaPendenteId ? `Pagar parcela ${p.numero}` : 'Pague as parcelas em ordem — use Amortizar extra pra adiantar'}
                        tone="success" disabled={!p.ehEntrada && p.id !== proximaPendenteId}
                        onClick={() => { setPagando(p); setPagamento({ dataPagamento: new Date().toISOString().slice(0, 10), desconto: '', multa: '' }); setErro(null) }}
                      />
                    </>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Modal
        open={!!pagando}
        onClose={() => setPagando(null)}
        title={`Pagar ${pagando?.ehEntrada ? 'entrada' : `parcela ${pagando?.numero}`}`}
        footer={<><Button variant="light" onClick={() => setPagando(null)}>Cancelar</Button><Button icon="ri-check-line" loading={pagar.isPending} onClick={() => pagar.mutate()}>Confirmar pagamento</Button></>}
      >
        <div className="space-y-3">
          <Input label="Data do pagamento" type="date" value={pagamento.dataPagamento} onChange={(e) => setPagamento((p) => ({ ...p, dataPagamento: e.target.value }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Desconto (R$, opcional)" type="number" min={0} step="0.01" value={pagamento.desconto} onChange={(e) => setPagamento((p) => ({ ...p, desconto: e.target.value }))} placeholder="Pagou antes" />
            <Input label="Multa (R$, opcional)" type="number" min={0} step="0.01" value={pagamento.multa} onChange={(e) => setPagamento((p) => ({ ...p, multa: e.target.value }))} placeholder="Pagou atrasado" />
          </div>
          {pagando && (
            <p className="text-2xs text-muted">
              Valor da parcela: {formatMoney(pagando.valor)} · Valor líquido: {formatMoney(pagando.valor - Number(pagamento.desconto || 0) + Number(pagamento.multa || 0))}
            </p>
          )}
          {erro && <p className="text-13 text-expense">{erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title={`Editar ${editando?.ehEntrada ? 'entrada' : `parcela ${editando?.numero}`}`}
        footer={<><Button variant="light" onClick={() => setEditando(null)}>Cancelar</Button><Button icon="ri-save-line" loading={editar.isPending} onClick={() => editar.mutate()}>Salvar</Button></>}
      >
        <div className="space-y-3">
          <Input label="Data prevista" type="date" value={edicao.data} onChange={(e) => setEdicao((v) => ({ ...v, data: e.target.value }))} />
          <Input label="Valor (R$)" type="number" min={0} step="0.01" value={edicao.valor} onChange={(e) => setEdicao((v) => ({ ...v, valor: e.target.value }))} />
          {erro && <p className="text-13 text-expense">{erro}</p>}
        </div>
      </Modal>

      <Modal
        open={marcandoEmMassa}
        onClose={() => setMarcandoEmMassa(false)}
        title="Marcar parcelas antigas como pagas"
        footer={<><Button variant="light" onClick={() => setMarcandoEmMassa(false)}>Cancelar</Button><Button icon="ri-list-check-3" loading={marcarEmMassa.isPending} onClick={() => marcarEmMassa.mutate()}>Marcar</Button></>}
      >
        <div className="space-y-3">
          <p className="text-13 text-muted">Para cadastrar um financiamento antigo: marca de uma vez as parcelas já pagas fora do app. Elas não geram lançamento nem entram em relatórios.</p>
          <Input label="Até esta data (inclusive)" type="date" value={emMassa.ate} onChange={(e) => setEmMassa((v) => ({ ...v, ate: e.target.value, ateNumero: '' }))} />
          <Input label="Ou até esta parcela nº (inclusive)" type="number" min={1} value={emMassa.ateNumero} onChange={(e) => setEmMassa((v) => ({ ...v, ateNumero: e.target.value, ate: '' }))} />
          <Input label="Data de pagamento registrada (opcional)" type="date" value={emMassa.dataPagamento} onChange={(e) => setEmMassa((v) => ({ ...v, dataPagamento: e.target.value }))} />
          {erro && <p className="text-13 text-expense">{erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={quitando}
        onClose={() => setQuitando(false)}
        onConfirm={() => quitar.mutate()}
        loading={quitar.isPending}
        title="Quitar financiamento antecipadamente?"
        confirmLabel="Sim, quitar"
        message={
          <div className="space-y-3 text-left">
            <p>As parcelas em aberto serão canceladas e substituídas por um único lançamento de quitação.</p>
            <Input label="Valor da quitação (R$)" type="number" min={0} step="0.01" value={quitacao.valorQuitacao} onChange={(e) => setQuitacao((v) => ({ ...v, valorQuitacao: e.target.value }))} />
            <Input label="Data" type="date" value={quitacao.data} onChange={(e) => setQuitacao((v) => ({ ...v, data: e.target.value }))} />
            {erro && <p className="text-13 text-expense">{erro}</p>}
          </div>
        }
      />

      {amortizandoExtra && (
        <AmortizacaoExtraModal financiamentoId={id} onClose={() => setAmortizandoExtra(false)} onAplicado={invalidar} />
      )}
      {corrigindoManual && (
        <CorrecaoManualModal financiamentoId={id} onClose={() => setCorrigindoManual(false)} onAplicado={invalidar} />
      )}
      {editandoFinanciamento && (
        <EditarFinanciamentoModal financiamento={f} onClose={() => setEditandoFinanciamento(false)} onSalvo={invalidar} />
      )}
      {gerenciandoExtras && (
        <GerenciarAmortizacoesExtrasModal financiamentoId={id} onClose={() => setGerenciandoExtras(false)} onAlterado={invalidar} />
      )}
    </>
  )
}

function EditarFinanciamentoModal({ financiamento, onClose, onSalvo }: { financiamento: Financiamento; onClose: () => void; onSalvo: () => void }) {
  const toast = useToast()
  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list({ incluirInativas: false }) })
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list() })

  const [descricao, setDescricao] = useState(financiamento.descricao)
  const [instituicao, setInstituicao] = useState(financiamento.instituicao ?? '')
  const [accountId, setAccountId] = useState(financiamento.accountId)
  const [categoriaId, setCategoriaId] = useState(financiamento.categoriaId ?? '')
  const [valorTotal, setValorTotal] = useState(String(financiamento.valorTotal))
  const [valorEntrada, setValorEntrada] = useState(financiamento.valorEntrada != null ? String(financiamento.valorEntrada) : '')
  const [sistemaAmortizacao, setSistemaAmortizacao] = useState(financiamento.sistemaAmortizacao)
  const [taxaJuros, setTaxaJuros] = useState(financiamento.taxaJurosMensal != null ? String(financiamento.taxaJurosMensal * 100) : '')
  const [tipoTaxa, setTipoTaxa] = useState<'mensal' | 'anual'>('mensal')
  const [indiceCorrecao, setIndiceCorrecao] = useState(financiamento.indiceCorrecao)
  const [anexoUrl, setAnexoUrl] = useState(financiamento.anexoUrl ?? '')
  const [erro, setErro] = useState<string | null>(null)

  const salvar = useMutation({
    mutationFn: () => {
      const taxa = taxaJuros ? Number(taxaJuros) / 100 : undefined
      return financiamentosApi.atualizar(financiamento.id, {
        descricao, instituicao: instituicao || undefined, accountId, categoriaId: categoriaId || undefined,
        valorTotal: Number(valorTotal), valorEntrada: valorEntrada ? Number(valorEntrada) : undefined,
        sistemaAmortizacao,
        taxaJurosMensal: sistemaAmortizacao !== SistemaAmortizacao.Manual && tipoTaxa === 'mensal' ? taxa : undefined,
        taxaJurosAnual: sistemaAmortizacao !== SistemaAmortizacao.Manual && tipoTaxa === 'anual' ? taxa : undefined,
        indiceCorrecao, anexoUrl: anexoUrl || undefined,
      })
    },
    onSuccess: () => { onSalvo(); toast.success('Financiamento atualizado.'); onClose() },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title="Editar financiamento" size="lg"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-save-line" loading={salvar.isPending} onClick={() => salvar.mutate()}>Salvar</Button></>}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Descrição" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
        <Input label="Instituição" value={instituicao} onChange={(e) => setInstituicao(e.target.value)} />
        <Select label="Conta" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          {contas.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.institution}</option>)}
        </Select>
        <Select label="Categoria" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
          <option value="">Sem categoria</option>
          {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
        </Select>
        <Input label="Valor total (R$)" type="number" min={0} step="0.01" value={valorTotal} onChange={(e) => setValorTotal(e.target.value)} />
        <Input label="Valor da entrada (R$, opcional)" type="number" min={0} step="0.01" value={valorEntrada} onChange={(e) => setValorEntrada(e.target.value)} />
        <Select label="Sistema de amortização" value={sistemaAmortizacao} onChange={(e) => setSistemaAmortizacao(Number(e.target.value) as SistemaAmortizacao)}>
          {Object.entries(SISTEMA_AMORTIZACAO_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </Select>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="Taxa de juros (%)" type="number" min={0} step="0.01" value={taxaJuros}
              disabled={sistemaAmortizacao === SistemaAmortizacao.Manual}
              onChange={(e) => setTaxaJuros(e.target.value)}
            />
          </div>
          <Select label="&nbsp;" value={tipoTaxa} disabled={sistemaAmortizacao === SistemaAmortizacao.Manual} onChange={(e) => setTipoTaxa(e.target.value as 'mensal' | 'anual')} className="w-28">
            <option value="mensal">ao mês</option>
            <option value="anual">ao ano</option>
          </Select>
        </div>
        {sistemaAmortizacao !== SistemaAmortizacao.Manual && (
          <Select label="Correção monetária" value={indiceCorrecao} onChange={(e) => setIndiceCorrecao(Number(e.target.value) as IndiceCorrecaoMonetaria)}>
            {Object.entries(INDICE_CORRECAO_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </Select>
        )}
        <Input label="URL do contrato (PDF, opcional)" icon="ri-attachment-2" value={anexoUrl} onChange={(e) => setAnexoUrl(e.target.value)} className="sm:col-span-2" />
      </div>
      <p className="mt-3 text-2xs text-muted">Mudar valor, entrada, sistema ou taxa recalcula só as parcelas ainda em aberto — as já pagas não mudam. Periodicidade e quantidade de parcelas não podem ser alteradas aqui.</p>
      {erro && <p className="mt-3 flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
    </Modal>
  )
}

function AmortizacaoExtraModal({ financiamentoId, onClose, onAplicado }: { financiamentoId: string; onClose: () => void; onAplicado: () => void }) {
  const toast = useToast()
  const [valorExtra, setValorExtra] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [estrategia, setEstrategia] = useState<EstrategiaAmortizacaoExtra>(EstrategiaAmortizacaoExtra.ReduzirPrazo)
  const [resultado, setResultado] = useState<AmortizacaoExtraResultado | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const payload = () => ({ valorExtra: Number(valorExtra), data, estrategia })

  const simular = useMutation({
    mutationFn: () => financiamentosApi.simularAmortizacaoExtra(financiamentoId, payload()),
    onSuccess: (r) => { setResultado(r); setErro(null) },
    onError: (e: Error) => { setErro(e.message); setResultado(null) },
  })

  const aplicar = useMutation({
    mutationFn: () => financiamentosApi.aplicarAmortizacaoExtra(financiamentoId, payload()),
    onSuccess: () => { onAplicado(); toast.success('Amortização extra aplicada.'); onClose() },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title="Amortizar extra"
      footer={
        <>
          <Button variant="light" onClick={onClose}>Cancelar</Button>
          <Button variant="soft-info" icon="ri-calculator-line" loading={simular.isPending} disabled={!valorExtra} onClick={() => simular.mutate()}>Simular</Button>
          <Button icon="ri-check-line" loading={aplicar.isPending} disabled={!resultado} onClick={() => aplicar.mutate()}>Aplicar de verdade</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Valor extra (R$)" type="number" min={0} step="0.01" value={valorExtra} onChange={(e) => { setValorExtra(e.target.value); setResultado(null) }} />
        <Input label="Data" type="date" value={data} onChange={(e) => { setData(e.target.value); setResultado(null) }} />
        <Select label="O que fazer com o tempo/valor liberado" value={estrategia} onChange={(e) => { setEstrategia(Number(e.target.value) as EstrategiaAmortizacaoExtra); setResultado(null) }}>
          {Object.entries(ESTRATEGIA_AMORTIZACAO_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </Select>

        {resultado && (
          <div className="grid grid-cols-2 gap-3 rounded border border-border/70 p-3 text-13">
            <div>
              <p className="text-2xs uppercase text-muted">Parcelas restantes</p>
              <p className="text-white">{resultado.parcelasRestantesAntes} <i className="ri-arrow-right-line text-muted" aria-hidden /> <strong>{resultado.parcelasRestantesDepois}</strong></p>
            </div>
            <div>
              <p className="text-2xs uppercase text-muted">Próxima parcela</p>
              <p className="text-white">{formatMoney(resultado.valorProximaParcelaAntes)} <i className="ri-arrow-right-line text-muted" aria-hidden /> <strong>{formatMoney(resultado.valorProximaParcelaDepois)}</strong></p>
            </div>
            <div>
              <p className="text-2xs uppercase text-muted">Término</p>
              <p className="text-white">{formatDate(resultado.dataTerminoAntes)} <i className="ri-arrow-right-line text-muted" aria-hidden /> <strong>{formatDate(resultado.dataTerminoDepois)}</strong></p>
            </div>
            <div>
              <p className="text-2xs uppercase text-muted">Economia de juros</p>
              <p className="font-semibold text-income">{formatMoney(resultado.economiaJuros)}</p>
            </div>
          </div>
        )}

        {erro && <p className="text-13 text-expense">{erro}</p>}
      </div>
    </Modal>
  )
}

function CorrecaoManualModal({ financiamentoId, onClose, onAplicado }: { financiamentoId: string; onClose: () => void; onAplicado: () => void }) {
  const toast = useToast()
  const hoje = new Date()
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [ano, setAno] = useState(hoje.getFullYear())
  const [indiceValor, setIndiceValor] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const corrigir = useMutation({
    mutationFn: () => financiamentosApi.corrigirManualmente(financiamentoId, { mes, ano, indiceValor: Number(indiceValor) / 100 }),
    onSuccess: () => { onAplicado(); toast.success('Correção aplicada.'); onClose() },
    onError: (e: Error) => setErro(e.message),
  })

  return (
    <Modal
      open onClose={onClose} title="Corrigir índice manualmente"
      footer={<><Button variant="light" onClick={onClose}>Cancelar</Button><Button icon="ri-check-line" loading={corrigir.isPending} disabled={!indiceValor} onClick={() => corrigir.mutate()}>Aplicar</Button></>}
    >
      <div className="space-y-3">
        <p className="text-13 text-muted">Sobrescreve o índice desse mês (mesmo que já tenha sido aplicado automaticamente) e recalcula as parcelas a partir dali.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Mês" type="number" min={1} max={12} value={mes} onChange={(e) => setMes(Number(e.target.value))} />
          <Input label="Ano" type="number" min={1900} value={ano} onChange={(e) => setAno(Number(e.target.value))} />
        </div>
        <Input label="Índice do mês (%)" type="number" step="0.0001" value={indiceValor} onChange={(e) => setIndiceValor(e.target.value)} placeholder="Ex: 0,16" />
        {erro && <p className="text-13 text-expense">{erro}</p>}
      </div>
    </Modal>
  )
}

function GerenciarAmortizacoesExtrasModal({ financiamentoId, onClose, onAlterado }: { financiamentoId: string; onClose: () => void; onAlterado: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [editando, setEditando] = useState<AmortizacaoExtraLancada | null>(null)
  const [edicao, setEdicao] = useState({ valor: '', data: '' })
  const [removendo, setRemovendo] = useState<AmortizacaoExtraLancada | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const { data: itens = [], isLoading } = useQuery({
    queryKey: ['financiamentos', financiamentoId, 'amortizacao-extra'],
    queryFn: () => financiamentosApi.listarAmortizacoesExtras(financiamentoId),
  })

  function recarregar() {
    qc.invalidateQueries({ queryKey: ['financiamentos', financiamentoId, 'amortizacao-extra'] })
    onAlterado()
  }

  const editar = useMutation({
    mutationFn: () => financiamentosApi.editarAmortizacaoExtra(financiamentoId, editando!.id, {
      valor: edicao.valor ? Number(edicao.valor) : undefined,
      data: edicao.data || undefined,
    }),
    onSuccess: () => { recarregar(); toast.success('Amortização extra atualizada.'); setEditando(null) },
    onError: (e: Error) => setErro(e.message),
  })

  const remover = useMutation({
    mutationFn: () => financiamentosApi.removerAmortizacaoExtra(financiamentoId, removendo!.id),
    onSuccess: () => { recarregar(); toast.success('Amortização extra removida — parcelas em aberto recalculadas.'); setRemovendo(null) },
    onError: (e: Error) => { toast.error(e.message); setRemovendo(null) },
  })

  return (
    <>
      <Modal open onClose={onClose} title="Amortizações extras lançadas" size="lg">
        {isLoading ? (
          <LoadingState />
        ) : itens.length === 0 ? (
          <p className="text-13 text-muted">Nenhuma amortização extra lançada neste financiamento.</p>
        ) : (
          <Table>
            <THead>
              <tr>
                <Th>Data</Th><Th className="text-right">Valor</Th><Th className="text-right">Ações</Th>
              </tr>
            </THead>
            <tbody>
              {itens.map((a) => (
                <Tr key={a.id}>
                  <Td className="whitespace-nowrap">{formatDate(a.data)}</Td>
                  <Td className="whitespace-nowrap text-right text-white">{formatMoney(a.valor)}</Td>
                  <Td className="whitespace-nowrap text-right">
                    <IconButton
                      icon="ri-pencil-fill" label="Editar" tone="info"
                      onClick={() => { setEditando(a); setEdicao({ valor: String(a.valor), data: a.data.slice(0, 10) }); setErro(null) }}
                    />
                    <IconButton icon="ri-delete-bin-line" label="Remover" tone="danger" onClick={() => setRemovendo(a)} />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
        <p className="mt-4 text-2xs text-muted">Editar ou remover recalcula as parcelas ainda em aberto a partir do saldo devedor atualizado. As parcelas já pagas não mudam.</p>
      </Modal>

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title="Editar amortização extra"
        footer={<><Button variant="light" onClick={() => setEditando(null)}>Cancelar</Button><Button icon="ri-save-line" loading={editar.isPending} onClick={() => editar.mutate()}>Salvar</Button></>}
      >
        <div className="space-y-3">
          <Input label="Valor (R$)" type="number" min={0} step="0.01" value={edicao.valor} onChange={(e) => setEdicao((v) => ({ ...v, valor: e.target.value }))} />
          <Input label="Data" type="date" value={edicao.data} onChange={(e) => setEdicao((v) => ({ ...v, data: e.target.value }))} />
          {erro && <p className="text-13 text-expense">{erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!removendo}
        onClose={() => setRemovendo(null)}
        onConfirm={() => remover.mutate()}
        loading={remover.isPending}
        title="Remover esta amortização extra?"
        confirmLabel="Sim, remover"
        message={
          removendo
            ? `A amortização de ${formatMoney(removendo.valor)} em ${formatDate(removendo.data)} será removida e as parcelas em aberto serão recalculadas a partir do saldo devedor atualizado.`
            : ''
        }
      />
    </>
  )
}
